// src/components/Catalogo/CatalogoPublico.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Row, Col, Card, Badge, Button, Form } from "react-bootstrap";
import api from "../../api/axios";
import { useAppToast } from "../Common/Toast/AppToastProvider";

import activoTier from "../../assets/tiers/activo.png";
import destacadoTier from "../../assets/tiers/destacado.png";
import premiumTier from "../../assets/tiers/premium.png";
import eliteTier from "../../assets/tiers/elite.png";

import BenefitCard from "../Home/BenefitCard";

// Tiers de negocio
const BUSINESS_TIERS = {
  ACTIVO: {
    label: "Local activo",
    variant: "secondary",
  },
  DESTACADO: {
    label: "Local destacado",
    variant: "info",
  },
  PREMIUM: {
    label: "Local premium",
    variant: "primary",
  },
  ELITE: {
    label: "Local elite",
    variant: "warning",
  },
};

const TIER_IMAGES = {
  ACTIVO: activoTier,
  DESTACADO: destacadoTier,
  PREMIUM: premiumTier,
  ELITE: eliteTier,
};

const getBusinessTierMeta = (tierCode) => {
  const code = (tierCode || "ACTIVO").toUpperCase();
  const base = BUSINESS_TIERS[code];

  if (base) {
    return {
      code,
      ...base,
    };
  }

  return {
    code,
    label: tierCode || "Local activo",
    variant: "secondary",
  };
};

const isActive = (c) =>
  !c.is_expired && (c.status === 1 || c.status === "active");

const flattenLegacyCatalog = (data) => {
  if (data?.coupons) return data.coupons;
  if (!data?.catalog) return [];
  const flat = [];
  Object.keys(data.catalog).forEach((cat) => {
    data.catalog[cat].forEach((p) =>
      flat.push({
        ...p,
        category: cat,
        is_expired:
          p.is_expired ??
          (p.valid_until ? (new Date(p.valid_until) < new Date() ? 1 : 0) : 0),
        status: p.status ?? 1,
      })
    );
  });
  return flat;
};

const CatalogoPublico = () => {
  const { showToast } = useAppToast();
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);

  // UI state
  const [query, setQuery] = useState("");
  const [showExpired, setShowExpired] = useState(false);
  const [sortBy, setSortBy] = useState("relevance"); // relevance | discount | expiring | newest
  const [category, setCategory] = useState("all");

  const token = localStorage.getItem("token");

  useEffect(() => {
    const fetchCatalog = async () => {
      try {
        const res = await api.get(`/catalogo/${slug}`);
        const couponsNormalized =
          res.data?.coupons || flattenLegacyCatalog(res.data) || [];
        setData(res.data);
        setCoupons(couponsNormalized);
      } catch (error) {
        console.error("Error al cargar catálogo", error);
        setData({ error: true });
      } finally {
        setLoading(false);
      }
    };
    fetchCatalog();
  }, [slug]);

  // Reclamar beneficio (idéntico a Home)
  const handleClaim = async (benefitId) => {
    if (!token) {
      showToast("Iniciá sesión para reclamar este beneficio.", "info", "Acción requerida");
      navigate(`/login?benefit=${benefitId}`);
      return;
    }

    try {
      const res = await api.post(
        `/benefits/claim/${benefitId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showToast(`Beneficio reclamado. Código: ${res.data.claimCode}`, "success", "Listo");

      // Actualizar listado
      setCoupons((prev) =>
        prev.map((c) => (c.id === benefitId ? { ...c, claimed: true } : c))
      );
    } catch (err) {
      console.error("Error al reclamar beneficio:", err);
      showToast(
        err?.response?.data?.message || "No se pudo reclamar el beneficio.",
        "danger",
        "Error"
      );
    }
  };

  const categories = useMemo(() => {
    const set = new Set();
    coupons.forEach((c) => c.category && set.add(c.category));
    return ["all", ...Array.from(set)];
  }, [coupons]);

  const filtered = useMemo(() => {
    let list = [...coupons];

    // Estado (activos vs vencidos)
    if (!showExpired) list = list.filter((c) => isActive(c));

    // Categoría
    if (category !== "all") list = list.filter((c) => c.category === category);

    // Búsqueda
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter(
        (c) =>
          c.name?.toLowerCase().includes(q) ||
          c.description?.toLowerCase().includes(q) ||
          c.category?.toLowerCase().includes(q)
      );
    }

    // Orden
    switch (sortBy) {
      case "discount":
        list.sort(
          (a, b) =>
            (b.discount_percentage || 0) - (a.discount_percentage || 0)
        );
        break;
      case "expiring":
        list.sort((a, b) => {
          const ad = a.valid_until
            ? new Date(a.valid_until).getTime()
            : Infinity;
          const bd = b.valid_until
            ? new Date(b.valid_until).getTime()
            : Infinity;
          return ad - bd;
        });
        break;
      case "newest":
        list.sort(
          (a, b) => (b.created_at || b.id || 0) - (a.created_at || a.id || 0)
        );
        break;
      default:
        break;
    }

    return list;
  }, [coupons, query, showExpired, sortBy, category]);

  // Skeleton mientras carga
  if (loading) {
    const skeletonItems = Array.from({ length: 8 });

    return (
      <div className="container mt-4">
        <Row xs={1} sm={2} md={3} lg={4} className="g-3">
          {skeletonItems.map((_, idx) => (
            <Col key={idx}>
              <Card className="h-100 shadow-sm">
                {/* Imagen skeleton */}
                <div
                  style={{
                    height: 160,
                    background: "#e5e7eb",
                  }}
                />
                <Card.Body className="d-flex flex-column">
                  {/* Título */}
                  <div
                    style={{
                      height: 14,
                      width: "70%",
                      background: "#e5e7eb",
                      borderRadius: 4,
                      marginBottom: 8,
                    }}
                  />
                  {/* Categoria / tipo */}
                  <div
                    style={{
                      height: 12,
                      width: "40%",
                      background: "#e5e7eb",
                      borderRadius: 4,
                      marginBottom: 12,
                    }}
                  />
                  {/* Descripción líneas */}
                  <div
                    style={{
                      height: 10,
                      width: "90%",
                      background: "#e5e7eb",
                      borderRadius: 4,
                      marginBottom: 6,
                    }}
                  />
                  <div
                    style={{
                      height: 10,
                      width: "80%",
                      background: "#e5e7eb",
                      borderRadius: 4,
                      marginBottom: 6,
                    }}
                  />
                  {/* Botón */}
                  <div className="mt-auto">
                    <div
                      style={{
                        height: 32,
                        width: "100%",
                        background: "#e5e7eb",
                        borderRadius: 6,
                      }}
                    />
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      </div>
    );
  }

  if (data?.error)
    return (
      <p className="text-center mt-5 text-danger">
        No se pudo cargar el catálogo.
      </p>
    );
  if (!data?.business)
    return <p className="text-center mt-5">Comercio no encontrado.</p>;

  const activeCount = coupons.filter(isActive).length;
  const totalCoupons = coupons.length;
  const maxDiscount = coupons.reduce(
    (max, c) =>
      typeof c.discount_percentage === "number"
        ? Math.max(max, c.discount_percentage)
        : max,
    0
  );

  const tierMeta = getBusinessTierMeta(data.business.tier_code);
  const tierImgSrc = TIER_IMAGES[tierMeta.code];

  return (
    <div className="container mt-4">
      {/* Header comercio */}
      <Card className="mb-4 shadow-sm">
        <Card.Body className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
          <div className="d-flex align-items-center gap-3">
            {data.business.logo_url && (
              <img
                src={data.business.logo_url}
                alt={data.business.name}
                style={{
                  width: 64,
                  height: 64,
                  objectFit: "cover",
                  borderRadius: "50%",
                }}
              />
            )}
            <div>
              <h2 className="m-0" style={{ fontSize: "1.4rem" }}>
                {data.business.name}
              </h2>

              {/* Tier del local */}
              <div className="d-flex align-items-center flex-wrap gap-2 mt-1">
                {tierImgSrc && (
                  <div className="d-flex align-items-center gap-2">
                    <img
                      src={tierImgSrc}
                      alt={tierMeta.label}
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        objectFit: "cover",
                        boxShadow: "0 0 6px rgba(0,0,0,0.15)",
                      }}
                    />
                    <span
                      className="text-muted"
                      style={{ fontSize: ".85rem", fontWeight: 600 }}
                    >
                      {tierMeta.label}
                    </span>
                  </div>
                )}

                {activeCount > 0 && (
                  <Badge bg="success">
                    {activeCount} beneficios activos
                  </Badge>
                )}

                {totalCoupons > activeCount && (
                  <Badge bg="secondary">
                    {totalCoupons} beneficios en total
                  </Badge>
                )}

                {maxDiscount > 0 && (
                  <Badge bg="danger">Hasta -{maxDiscount}% OFF</Badge>
                )}
              </div>

              {/* Dirección y contexto */}
              <div className="mt-1" style={{ fontSize: ".85rem" }}>
                {data.business.address && (
                  <>
                    <span className="text-muted">
                      📍 {data.business.address} - {data.business.city}
                    </span>
                    {" · "}
                  </>
                )}
                <br />
                <span className="text-muted">
                  Este local forma parte del programa de puntos de QR Benefits.
                </span>
              </div>
            </div>
          </div>

          <div className="d-flex flex-column flex-sm-row gap-2 ms-md-auto">
            {data.business.address && (
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => {
                  const q = encodeURIComponent(data.business.address);
                  window.open(
                    `https://www.google.com/maps/search/?api=1&query=${q}`,
                    "_blank"
                  );
                }}
              >
                Ver en mapa
              </Button>
            )}

            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => navigate(-1)}
            >
              ← Volver
            </Button>
          </div>
        </Card.Body>
      </Card>

      {/* Controles */}
      <Row className="gy-2 gx-2 align-items-end mb-3">
        <Col xs={12} md={4}>
          <Form.Label>Buscar</Form.Label>
          <Form.Control
            placeholder="Ej: 2x1 café, pizza, estética…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </Col>
        <Col xs={12} md={3}>
          <Form.Label>Categoría</Form.Label>
          <Form.Select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            {categories.map((c) => (
              <option key={c} value={c}>
                {c === "all" ? "Todas" : c}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col xs={12} md={3}>
          <Form.Label>Ordenar por</Form.Label>
          <Form.Select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="relevance">Relevancia</option>
            <option value="discount">Mayor descuento</option>
            <option value="expiring">Próximos a vencer</option>
            <option value="newest">Más nuevos</option>
          </Form.Select>
        </Col>
        <Col xs={12} md={2} className="d-flex align-items-center">
          <Form.Check
            type="switch"
            id="showExpired"
            label="Mostrar vencidos"
            checked={showExpired}
            onChange={(e) => setShowExpired(e.target.checked)}
          />
        </Col>
      </Row>

      {/* Grid de cupones */}
      {filtered.length === 0 ? (
        <div className="text-center text-muted py-5">
          No encontramos cupones con esos filtros.
        </div>
      ) : (
        <Row xs={1} sm={2} md={3} lg={4} className="g-3">
          {filtered.map((c) => (
            <Col key={c.id}>
              <BenefitCard
                benefit={c}
                comercio={data.business}
                onClaim={handleClaim}
                showBusinessLink={false} // ya estamos dentro del catálogo del comercio
              />
            </Col>
          ))}
        </Row>
      )}
    </div>
  );
};

export default CatalogoPublico;
